import { useTranslation } from 'react-i18next';
import { Steps, StepsProps, Typography } from 'antd';
import { CLAIM_STATUSES, StatusHistory } from 'all-in-one-shared-api';

import styled from 'styled-components';

const { Step } = Steps;

interface StatusHistoryNA extends StatusHistory {
  claimId?: string;
}

export interface ClaimHistoryStepsProps {
  steps: StatusHistoryNA[];
}

const BravoSteps = styled(({ latestStatus, ...rest }) => {
  return <Steps {...rest} />;
})<StepsProps>`
  ${({ latestStatus }) => {
    switch (latestStatus) {
      case CLAIM_STATUSES.SUBMITTED:
      case CLAIM_STATUSES.IN_PROGRESS:
      case CLAIM_STATUSES.COMPLETED:
        return `
          .ant-steps-item-process,
          .ant-steps-item-finish {
            .ant-steps-item-icon .ant-steps-icon-dot {
              background: var(--neutral-6);
            }
            .ant-steps-item-container > .ant-steps-item-tail::after {
              background-color: var(--neutral-6);
            }
          }
          .ant-steps-item-active{
            .ant-steps-item-icon .ant-steps-icon-dot {
              background: var(--success-color);
            }
            .ant-steps-item-content {
              .ant-steps-item-title {
                font-weight: 500;
              }
              .ant-steps-item-description{
                color: rgba(0, 0 , 0, 0.45);
              }
            }
          }
        `;
      case CLAIM_STATUSES.DECLINED:
      case CLAIM_STATUSES.SHORTFALL:
        return `
          .ant-steps-item-process,
          .ant-steps-item-finish {
            .ant-steps-item-icon .ant-steps-icon-dot {
              background: var(--error-color);
            }
          }
          .ant-steps-item-finish {
            .ant-steps-item-container > .ant-steps-item-tail::after {
              background-color: var(--error-color);
            }
          }
        `;
      case CLAIM_STATUSES.NEED_ACTION:
        return `
          .ant-steps-item-process,
          .ant-steps-item-finish {
            .ant-steps-item-icon .ant-steps-icon-dot {
              background: var(--warning-color);
            }
          }
          .ant-steps-item-finish {
            .ant-steps-item-container > .ant-steps-item-tail::after {
              background-color: var(--warning-color);
            }
          }
        `;
      default:
        return;
    }
  }}
`;

type StepDescProps = {
  date: string;
  claimId?: string;
};
const StepDescription = ({ date, claimId }: StepDescProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Typography.Text type="secondary"> {t('claimDetails.dateOn', { date })} </Typography.Text>
      {claimId && <Typography.Text type="secondary">{`${t('claimDetails.claimNo')} ${claimId}`}</Typography.Text>}
    </div>
  );
};

const ClaimHistorySteps = ({ steps }: ClaimHistoryStepsProps): JSX.Element => {
  const { t } = useTranslation();
  const lastStep = steps?.[steps.length - 1] ?? {};

  return (
    <BravoSteps
      current={steps.length > 0 ? steps.length - 1 : 0}
      latestStatus={lastStep.status ?? ''}
      progressDot
      direction="vertical"
      size="small"
    >
      {steps.map(({ date, status, claimId }) => (
        <Step
          key={status}
          title={status && CLAIM_STATUSES[status] ? t(`enum.claimStatus.${CLAIM_STATUSES[status]}`) : ''}
          description={<StepDescription date={date} claimId={claimId} />}
        />
      ))}
    </BravoSteps>
  );
};

export default ClaimHistorySteps;
